/**
 * Lekki parser markdown dla artykułów blogowych. Nie używamy zewnętrznej biblioteki, bo
 * potrzebujemy tylko: h2/h3 z anchorem, listy, pogrubienia, linki, blockquote, akapity.
 *
 * Format anchorów: linia "## {anchor-id} Tytuł" → <h2 id="anchor-id">Tytuł</h2>
 */
import { Link } from "@tanstack/react-router";

interface Block {
  type: "h2" | "h3" | "p" | "ul" | "ol" | "quote";
  id?: string;
  text?: string;
  items?: string[];
}

function parseBlocks(md: string): Block[] {
  const lines = md.split("\n");
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i].trimEnd();
    if (!line.trim()) {
      i++;
      continue;
    }
    // h2 with anchor: ## {id} Tytuł
    const h2 = line.match(/^##\s+(?:\{([\w-]+)\}\s+)?(.+)$/);
    if (h2 && !line.startsWith("###")) {
      blocks.push({ type: "h2", id: h2[1], text: h2[2] });
      i++;
      continue;
    }
    const h3 = line.match(/^###\s+(.+)$/);
    if (h3) {
      blocks.push({ type: "h3", text: h3[1] });
      i++;
      continue;
    }
    if (line.startsWith("> ")) {
      const items: string[] = [];
      while (i < lines.length && lines[i].startsWith("> ")) {
        items.push(lines[i].slice(2));
        i++;
      }
      blocks.push({ type: "quote", text: items.join(" ") });
      continue;
    }
    if (/^[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^[-*]\s+/, ""));
        i++;
      }
      blocks.push({ type: "ul", items });
      continue;
    }
    if (/^\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s+/, ""));
        i++;
      }
      blocks.push({ type: "ol", items });
      continue;
    }
    // paragraph: zbierz do pustej linii
    const paraLines: string[] = [line];
    i++;
    while (i < lines.length && lines[i].trim() && !/^(#{2,3}\s|[-*]\s|>\s|\d+\.\s)/.test(lines[i])) {
      paraLines.push(lines[i]);
      i++;
    }
    blocks.push({ type: "p", text: paraLines.join(" ") });
  }
  return blocks;
}

/** Renderuj inline: **bold**, [tekst](url) */
function renderInline(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  // tokenize bold + links
  const regex = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) {
      parts.push(
        <strong key={key++} className="font-semibold text-foreground">
          {tok.slice(2, -2)}
        </strong>,
      );
    } else {
      const lm = tok.match(/^\[([^\]]+)\]\(([^)]+)\)$/)!;
      const [, label, href] = lm;
      const isInternal = href.startsWith("/");
      if (isInternal) {
        parts.push(
          <a key={key++} href={href} className="text-primary underline-offset-4 hover:underline">
            {label}
          </a>,
        );
      } else {
        parts.push(
          <a key={key++} href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline-offset-4 hover:underline">
            {label}
          </a>,
        );
      }
    }
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

export function MarkdownLite({ source }: { source: string }) {
  const blocks = parseBlocks(source);
  return (
    <div className="prose-content space-y-5">
      {blocks.map((b, idx) => {
        switch (b.type) {
          case "h2":
            return (
              <h2
                key={idx}
                id={b.id}
                className="mt-12 scroll-mt-28 font-display text-2xl font-bold md:text-3xl"
              >
                {b.text}
              </h2>
            );
          case "h3":
            return (
              <h3 key={idx} className="mt-8 font-display text-xl font-semibold">
                {b.text}
              </h3>
            );
          case "p":
            return (
              <p key={idx} className="leading-relaxed text-muted-foreground">
                {renderInline(b.text!)}
              </p>
            );
          case "ul":
            return (
              <ul key={idx} className="ml-5 list-disc space-y-2 text-muted-foreground">
                {b.items!.map((it, j) => (
                  <li key={j}>{renderInline(it)}</li>
                ))}
              </ul>
            );
          case "ol":
            return (
              <ol key={idx} className="ml-5 list-decimal space-y-2 text-muted-foreground">
                {b.items!.map((it, j) => (
                  <li key={j}>{renderInline(it)}</li>
                ))}
              </ol>
            );
          case "quote":
            return (
              <blockquote
                key={idx}
                className="rounded-xl border-l-4 border-primary bg-primary/5 px-5 py-4 text-foreground"
              >
                {renderInline(b.text!)}
              </blockquote>
            );
        }
      })}
    </div>
  );
}
